// src/components/organisation/renderers/NewsSectionRenderer.jsx
import React from 'react';
import { Calendar, User, ArrowRight, ExternalLink } from 'lucide-react';

export const NewsSectionRenderer = ({ 
  data, 
  bookData, 
  customStyling = {}, 
  onInteraction 
}) => {
  const {
    heading = 'Latest News',
    subHeading = '',
    articles = [],
    layout = 'grid',
    enabled = true
  } = data || {};

  if (!enabled || !articles?.length) return null;

  const trackInteraction = (action, details = {}) => {
    if (onInteraction) {
      onInteraction({
        type: 'news_interaction',
        action,
        section: 'news',
        sectionId: data?.id,
        ...details
      });
    }
  };

  const handleArticleClick = (article) => {
    trackInteraction('article_click', { articleTitle: article.title });
    if (article.url) {
      window.open(article.url, '_blank', 'noopener,noreferrer');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <section className="news-section py-16 bg-white">
      <div className="container mx-auto px-4">
        {/* Section header */}
        {(heading || subHeading) && (
          <div className="text-center mb-16">
            {heading && (
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {heading}
              </h2>
            )}
            {subHeading && (
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {subHeading}
              </p>
            )}
          </div>
        )}

        {/* News articles */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {articles.map((article, index) => (
            <article 
              key={index}
              className="news-article bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
              onClick={() => handleArticleClick(article)}
            >
              {/* Article image */}
              {article.image && (
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              )}
              
              {/* Article content */}
              <div className="p-6">
                {/* Category */}
                {article.category && (
                  <span className="inline-block bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full mb-4">
                    {article.category}
                  </span>
                )}

                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                  {article.title}
                </h3>

                {article.summary && (
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {article.summary}
                  </p>
                )}

                {/* Article meta */}
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <div className="flex items-center gap-4">
                    {article.author && (
                      <div className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        {article.author}
                      </div>
                    )}
                    {article.publishedAt && (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatDate(article.publishedAt)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Read more link */}
                <div className="flex items-center text-blue-600 font-medium group-hover:text-blue-700">
                  <span className="mr-2">Read more</span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};